<template>
    <div v-if="can">
        <settings-heading
            :title="$t('settings.roles.title')"
            :description="$t('settings.roles.description')"
        />

        <banner
            v-if="!currentUserIsAdmin"
            :description="$t('settings.roles.perm_warning')"
            inline
            warning
        />

        <div class="mb-48">

            <loading v-if="fetchingRoles" class="pt-16"></loading>

            <div v-else>
                <div class="flex">
                    <div class="w-1/4 border-r border-primary-20 px-8 py-16">
                        <div v-for="role in roles"
                             :class="editing && editing.id === role.id ? 'bg-primary-5 font-semibold' : ''" class="rounded-6 hover:bg-primary-5 px-16 py-8 trans-ease cursor-pointer mb-4 select-none flex items-center justify-between" @click="edit(role)">
                            {{ role.name }}
                            <svg-icon
                                class="text-primary-80"
                                size="16"
                                type="lock"
                                v-if="role.locked"
                            />
                        </div>
                        <simple-button
                            class="w-full mt-8"
                            icon="add-circle"
                            small
                            v-if="currentUserIsAdmin"
                            @click="create"
                        >
                            {{ $t('settings.roles.add') }}
                        </simple-button>
                    </div>
                    <div v-if="editing || creating" class="w-3/4">
                        <banner
                            v-if="editing && editing.locked_name"
                            :title="editing.name"
                            :description="$t('permissions.description.' + editing.locked_name)"
                            inline
                            info
                            class="mx-16"
                        />
                        <simple-input v-else ref="name" v-model="form.name"
                                      :disabled="!currentUserIsAdmin" :placeholder="$t('settings.roles.name')" class="mb-8 px-16">
                            {{ $t('settings.roles.name') }}
                        </simple-input>
                        <div class="flex flex-wrap mb-8 px-8">
                            <div v-for="group in allPermissions" :class="!group.fullWidth ? 'w-1/2' : 'w-full'" class="p-8">
                                <div class="py-8 px-16 bg-primary-5 rounded-6 h-full">
                                    <p class="font-semibold text-body mb-16">{{ $t(group.title) }}</p>
                                    <!--<p class="">{{ $t(group.description) }}</p>-->
                                    <div class="mt-8">

                                        <div v-for="permission in group.permissions">
                                            <checkbox v-if="permission !== 'jobs.view' && permission !== 'applications.view'"
                                                      :key="permission"
                                                      :checked="isPermissionActive(permission)"
                                                      :disabled="(editing && editing.locked) || (permission === 'jobs.create' && form.permissions.includes('jobs.manage')) || !currentUserIsAdmin"
                                                      class="mb-4" @change="togglePermission(permission)"
                                            >{{ $t('permissions.' + permission) }}
                                            </checkbox>


                                            <toggle
                                                :checked="isPermissionActive(permission)"
                                                class="mb-12"
                                                :disabled="(editing && editing.locked) || !currentUserIsAdmin"
                                                :key="permission"
                                                v-if="permission === 'jobs.view' || permission === 'applications.view'"
                                                @change="togglePermission(permission)"
                                            >
                                                {{ isPermissionActive(permission) ? $t('permissions.' + permission) : $t('permissions.' + permission + '_single') }}
                                            </toggle>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-if="currentUserIsAdmin" class="flex justify-between px-16">
                            <tooltip
                                v-if="editing && !editing.locked"
                                :content="$t('settings.roles.warning')"
                                :disabled="editing.users_count === 0"
                                position="bottom"
                            >
                                <simple-button
                                    :disabled="editing.users_count !== 0"
                                    display="danger"
                                    icon="delete"
                                    @click="confirmDelete(editing)"
                                >
                                    {{ $t('general.remove_subject', {subject: $t('settings.roles.role')}) }}
                                </simple-button>
                            </tooltip>

                            <simple-button
                                class="ml-auto"
                                :disabled="!form.isDirty()"
                                display="secondary"
                                icon="save"
                                :loading="savingRole"
                                look="primary"
                                v-if="!editing || !editing.locked"
                                @click="editing ? updateRole() : createRole()"
                            >
                                {{ $t('general.save') }}
                            </simple-button>
                        </div>
                    </div>
                    <div v-else class="w-3/4 flex items-center justify-center">
                        <p class="font-semibold text-primary-20 text-heading-2 select-none">Choose a role</p>
                    </div>
                </div>
            </div>
        </div>

        <popup-modal-confirm
            v-if="deleting"
            :confirmText="$t('general.remove_subject', {subject: $t('settings.roles.role')})"
            :message="confirmDeleteMessage(deleting)"
            show-exclamation
            :working="performingDeleteRequest"
            @close="cancelDelete"
            @confirm="removeRole(deleting)">
        </popup-modal-confirm>
    </div>
    <error-403 v-else></error-403>
</template>

<script>
import Loading from '../../components/ui/Loading.vue';
import SimpleButton from '../../components/ui/SimpleButton.vue';
import {normalizeJson} from '../../../shared/helpers';
import SvgIcon from '../../components/ui/SvgIcon.vue';
import {mapState, mapActions} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../../state/store';
import {useNotificationsStore} from '../../state/modules/notifications';
import PopupModalConfirm from '../../components/ui/PopupModalConfirm.vue';
import {ADD_NOTIFICATION} from '../../state/mutation-types';
import Error403 from '../../components/ui/Error403.vue';
import Role from '../../models/Role';
import * as roles from '../../services/RoleService';
import Checkbox from '../../components/forms/Checkbox';
import Input from '../../components/forms/Input';
import Form from '../../etc/forms/Form';
import Permission from '../../models/Permission';
import Tooltip from '../../components/navigation/Tooltip';
import Toggle from '../../components/forms/Toggle';
import Banner from "../../components/ui/Banner.vue";
import SettingsHeading from "../../components/ui/SettingsHeading.vue";


export default {
    components: {
        SettingsHeading,
        Banner,
        Tooltip,
        'simple-input': Input,
        Checkbox,
        'loading': Loading,
        'simple-button': SimpleButton,
        'svg-icon': SvgIcon,
        'popup-modal-confirm': PopupModalConfirm,
        'error-403': Error403,
        'toggle': Toggle,
    },
    data: function () {
        return {
            fetchingRoles: true,
            editing: null,
            creating: false,
            deleting: null,
            performingDeleteRequest: false,
            allPermissions: null,
            requiredPerms: null,
            form: null,
            savingRole: false,
        };
    },
    computed: {
        ...mapState(useMainStore, ['currentCompany', 'currentCompanyUser']),
        roles() {
            return useRepo(Role)
                .where('company_id', this.currentCompany.id)
                .with(['permissions'])
                .orderBy(role => {
                    switch (role.locked_name) {
                        case 'company_admin':
                            return 0;
                        case 'member':
                            return 1;
                        case 'marketeer':
                            return 2;
                        case 'recruiter':
                            return 3;
                        case 'accountant':
                            return 4;
                        default:
                            return role.name;
                    }
                })
                .get();
        },
        editTitle() {
            return this.$t('general.edit_subject', {subject: this.$t('settings.roles.role')});
        },
        removeTitle() {
            return this.$t('general.remove_subject', {subject: this.$t('settings.roles.role')});
        },
        can() {
            return this.$can('settings.general');
        },
        currentUserIsAdmin() {
            return this.currentCompanyUser?.is_owner;
        },
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        isPermissionActive(permission) {
            if (permission === 'jobs.create' && this.form.permissions.includes('jobs.manage')) {
                return true;
            }

            return this.form.permissions.includes(permission);
        },
        create() {
            this.form = new Form([
                {name: 'name', value: ''},
                {name: 'permissions', value: []},
            ]);

            this.creating = true;
            this.focusName();
            this.stopEdit();
        },
        stopCreate() {
            this.creating = false;
        },
        edit(role) {
            if (typeof role === 'number') {
                role = this.roles.find(i => i.id === role);
            }

            role = JSON.parse(JSON.stringify(role));
            this.form = new Form([
                {name: 'name', value: role.name},
                {name: 'permissions', value: role.permissions.map(perm => perm.action)},
            ]);

            this.editing = role;
            this.focusName();
            this.stopCreate();
        },
        focusName() {
            this.$nextTick(() => {
                if (this.$refs.name) {
                    this.$refs.name.focus();
                }
            });
        },
        stopEdit() {
            this.editing = null;
        },
        confirmDelete(role) {
            this.deleting = role;
        },
        cancelDelete() {
            this.deleting = null;
        },
        createRole() {
            this.savingRole = true;

            this.form.submit(data => roles.store({
                data: {
                    attributes: {
                        name: data.name,
                    },
                    relationships: {
                        permissions: data.permissions.map((perm) => ({
                            attributes: {
                                action: perm,
                            },
                        })),
                    },
                },
            })).then(result => {
                return useRepo(Role).save(normalizeJson(result));
            }).then(result => {
                this.edit(result.id);
                this.savingRole = false;
            });
        },
        updateRole() {
            this.savingRole = true;

            this.form.submit(data => roles.update(this.editing.id, {
                data: {
                    attributes: {
                        name: data.name,
                    },
                    relationships: {
                        permissions: data.permissions.map((perm) => ({
                            attributes: {
                                action: perm,
                            },
                        })),
                    },
                },
            })).then(async result => {
                await Promise.all(this.editing.permissions.map(i => useRepo(Permission).destroy(i.id)));
                return useRepo(Role).save(normalizeJson(result));
            }).then(result => {
                this.savingRole = false;
                this.edit(result.id);
            });
        },
        removeRole(role) {
            this.performingDeleteRequest = true;

            roles.destroy(role.id).then(() => {
                useRepo(Role).destroy(role.id);
            }).then(() => {
                this.deleting = null;
                this.performingDeleteRequest = false;
                this.stopEdit();
            }).then(() => {
                this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('general.removed', {subject: this.$t('settings.roles.role')}),
                });
            });
        },
        confirmDeleteMessage(role) {
            return this.$t('general.confirm_remove_subject', {subject: role.name});
        },
        togglePermission(permission) {
            if (this.form.permissions.includes(permission)) {
                this.form.permissions = this.form.permissions.filter(i => i !== permission);

                for (let perm in this.requiredPerms) {
                    if (!this.requiredPerms.hasOwnProperty(perm) || !this.requiredPerms[perm].includes(permission)) {
                        continue;
                    }

                    this.form.permissions = this.form.permissions.filter(i => perm !== i);
                }
            } else {
                this.form.permissions.push(permission);

                for (let perm in this.requiredPerms) {
                    if (!this.requiredPerms.hasOwnProperty(perm) || !permission.includes(perm)) {
                        continue;
                    }

                    this.requiredPerms[perm].forEach(permissionToAdd => {
                        if (!this.form.permissions.includes(permissionToAdd)) {
                            this.form.permissions.push(permissionToAdd);
                        }
                    });
                }
            }
        },
    },
    mounted() {
        this.fetchingRoles = true;

        roles.index().then(result => {
            this.allPermissions = result.permissions;
            this.requiredPerms = result.required_perms;
            return useRepo(Role).save(normalizeJson(result.data));
        }).then(inserts => {
            this.fetchingRoles = false;
        });
    },
};
</script>
